import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Paper,
  CircularProgress,
  Link,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { login, isAuthenticated } from '../../services/authService';
import { format } from 'date-fns';

/**
 * Login component for user authentication
 * @returns {JSX.Element} Login component
 */
const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoLogout, setAutoLogout] = useState(false);
  const [approvalBanner, setApprovalBanner] = useState(null);

  // Check if user is already logged in and handle redirects
  useEffect(() => {
    // Check if this is an auto-logout situation
    const params = new URLSearchParams(location.search);
    const isAutoLogout = params.get('auto_logout') === 'true';
    const isSessionTimeout = params.get('session_timeout') === 'true';
    const redirectPath = params.get('redirect') || '/';
    const approval = params.get('approval');

    if (approval === 'approved') {
      setApprovalBanner({ severity: 'success', message: 'Your account has been approved — you can now sign in.' });
    } else if (approval === 'rejected') {
      setApprovalBanner({ severity: 'warning', message: 'Your access request was not approved. Contact your team lead for assistance.' });
    }
    
    if (isSessionTimeout) {
      setAutoLogout(true);
      setError('Your session has timed out. Please log in again to continue.');
    } else if (isAutoLogout) {
      setAutoLogout(true);
      setError('Your session has expired due to inactivity. Please log in again.');
    }
    
    if (isAuthenticated()) {
      console.log('User is already authenticated, redirecting');
      navigate(redirectPath);
    }
  }, [location, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPendingStatus(null);
    setLoading(true);
    setAutoLogout(false);
    
    // Validate inputs
    if (!username.trim()) {
      setError('Username is required');
      setLoading(false);
      return;
    }
    
    if (!password) {
      setError('Password is required');
      setLoading(false);
      return;
    }
    
    console.log('Login form submitted for user:', username);
    
    try {
      await login(username, password, rememberMe);
      console.log('Login successful, redirecting');
      
      // Get the redirect path from URL parameters or default to home
      const params = new URLSearchParams(location.search);
      const redirectPath = params.get('redirect') || '/';
      
      // Use navigate instead of window.location for better React Router integration
      navigate(redirectPath);
    } catch (err) {
      console.error('Login error:', err);
      
      // Display user-friendly error message
      if (err.code === 'PENDING_APPROVAL' || err.message?.includes('pending approval')) {
        setPendingStatus({ submittedAt: err.submittedAt || null });
        setLoading(false);
        return;
      } else if (err.code === 'ACCESS_REJECTED' || err.message?.includes('not approved')) {
        setError('Your access request was not approved. Contact your team lead for assistance.');
      } else if (err.message.includes('Invalid credentials')) {
        setError('Invalid username or password. Please try again.');
      } else if (err.message.includes('Authentication failed')) {
        setError('Authentication failed. Please try again.');
      } else if (err.message.includes('JSON')) {
        setError('Server communication error. Please try again later.');
      } else if (err.message.includes('required')) {
        setError('Username and password are required.');
      } else if (err.message.includes('network') || err.message.includes('fetch')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'An error occurred during login. Please try again.');
      }
      
      setLoading(false);
    }
  };
  
  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      minHeight="100vh"
      bgcolor="background.default"
      sx={{
        background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
      }}
    >
      <Box
        sx={{
          maxWidth: 440,
          width: '100%',
          px: 2,
          animation: 'fadeInUp 0.4s ease-out',
        }}
      >
        <Paper 
          elevation={4}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <Card sx={{ boxShadow: 'none' }}>
            <CardContent sx={{ p: 4 }}>
              <Box textAlign="center" mb={4}>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: 'primary.main',
                  }}
                >
                  HighQ Metrics
                </Typography>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  Sign in to access the dashboard
                </Typography>
              </Box>
              
              {approvalBanner && (
                <Alert severity={approvalBanner.severity} sx={{ mb: 3, borderRadius: 2 }}>
                  {approvalBanner.message}
                </Alert>
              )}

              {pendingStatus && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Your account is pending approval
                  </Typography>
                  <Typography variant="body2">
                    {pendingStatus.submittedAt
                      ? `Request submitted on ${format(new Date(pendingStatus.submittedAt), 'dd MMM yyyy')}. `
                      : ''}
                    An administrator will review it shortly — check back here to sign in once approved.
                  </Typography>
                </Alert>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    animation: 'fadeIn 0.3s ease-out',
                  }}
                >
                  {error}
                </Alert>
              )}
              
              <form onSubmit={handleSubmit}>
                <TextField
                  label="Username"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  disabled={loading}
                  error={error.includes('username') || error.includes('Username')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                    mb: 2,
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  error={error.includes('password') || error.includes('Password')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                    mb: 1,
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" color="textSecondary">
                      Remember me for 5 days
                    </Typography>
                  }
                  sx={{ mb: 2, ml: 0 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Sign In'
                  )}
                </Button>
                
                <Box mt={3} textAlign="center">
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.5,
                      '& a': {
                        fontWeight: 500,
                        transition: 'color 0.2s',
                        '&:hover': { color: 'secondary.main' },
                      },
                    }}
                  >
                    <Link href="/reset-password" underline="hover">
                      Forgot your password?
                    </Link>
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    p: 2.5,
                    borderRadius: 2,
                    border: '2px solid #1D4434',
                    backgroundColor: 'rgba(29, 68, 52, 0.06)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1D4434', mb: 0.5 }}>
                    Need access?
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', color: 'text.primary', mb: 2, lineHeight: 1.5 }}>
                    This portal is for authorised users only. New here? Submit a request and a portal
                    administrator will review it, usually within 1&ndash;2 business days.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/signup"
                    variant="outlined"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.25,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      color: '#1D4434',
                      borderColor: '#1D4434',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: '#1D4434',
                        backgroundColor: 'rgba(29, 68, 52, 0.12)',
                      },
                    }}
                  >
                    Request access
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;
